import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { RadioProvider } from "@/context/RadioContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { RadioPlayer } from "@/components/site/RadioPlayer";
import { BackToTop } from "@/components/site/BackToTop";
import Home from "@/pages/Home";
import Radio from "@/pages/Radio";
import SharedFavorites from "@/pages/SharedFavorites";
import Unsubscribe from "@/pages/Unsubscribe";
import { PaymentCancel, PaymentSuccess } from "@/pages/Payment";
import Agenda from "@/pages/Agenda";
import { News } from "@/pages/Articles";
import Artists from "@/pages/Artists";
import Places from "@/pages/Places";
import Arrivages from "@/pages/Arrivages";
import Contact from "@/pages/Contact";
import Support from "@/pages/Support";
import Legal from "@/pages/Legal";
import EventDetail from "@/pages/EventDetail";
import ArticleDetail from "@/pages/ArticleDetail";
import ArtistDetail from "@/pages/ArtistDetail";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { AuthProvider } from "@/context/AuthContext";

const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const go = () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
    const timers = [150, 900].map((ms) => setTimeout(go, ms));
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  return null;
};

const BACK_TO_TOP_PATHS = /^\/(agenda|news|podcasts|artistes|radio)(\/|$)/;

const BackToTopOnLongPages = () => {
  const { pathname } = useLocation();
  return BACK_TO_TOP_PATHS.test(pathname) ? <BackToTop /> : null;
};

const Layout = () => (
  <>
    <ScrollTop />
    <Header />
    <main>
      <Outlet />
    </main>
    <Footer />
    <BackToTopOnLongPages />
    <RadioPlayer />
  </>
);

function App() {
  return (
    <div className="App">
      <RadioProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/radio" element={<Radio />} />
              <Route path="/favoris/:id" element={<SharedFavorites />} />
              <Route path="/desabonnement/:token" element={<Unsubscribe />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/agenda/:slug" element={<EventDetail />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<ArticleDetail kind="news" />} />
              <Route path="/podcasts" element={<Navigate to="/radio#podcasts" replace />} />
              <Route path="/podcasts/:slug" element={<ArticleDetail kind="podcasts" />} />
              <Route path="/artistes" element={<Artists />} />
              <Route path="/artistes/:slug" element={<ArtistDetail />} />
              <Route path="/lieux/:category" element={<Places />} />
              <Route path="/lieux" element={<Places />} />
              <Route path="/arrivages" element={<Arrivages />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/soutenir" element={<Support />} />
              <Route path="/mentions-legales" element={<Legal page="mentions-legales" />} />
              <Route path="/conditions-generales" element={<Legal page="conditions-generales" />} />
            </Route>
            <Route path="/admin/login" element={<AuthProvider><AdminLogin /></AuthProvider>} />
            <Route path="/admin" element={<AuthProvider><AdminDashboard /></AuthProvider>} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" toastOptions={{ className: "rounded-none" }} />
      </RadioProvider>
    </div>
  );
}

export default App;
